import Downshift, {
  ControllerStateAndHelpers,
  DownshiftInterfaceWithKeyDownHandlers,
} from 'downshift';
import { GetString } from 'fluent-react';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  rootFontSize,
} from '../../cssVariables';
import {
  getTotalTextHeight,
} from '../../sharedComponents/newPerformTextLayout';
import {
  offscreenRenderingDivID as defaultOffscreenRenderingDivID,
} from '../../Utils';
import {
  getCharacterWidthMap as defaultGetCharacterWidthMap,
} from './getCharacterWidthMap';
import getMaxScrollbarWidth from './getMaxScrollbarWidth';
import getRenderProp from './getRenderProp';
import {
  countryNameFontSize,
  flagToTextSeparation,
  flagWidth,
  getMenuItemLeftPadding,
  menuItemLineHeight,
} from './Menu';
import Prompt from './Prompt';
import {
  Option,
} from './query';

export enum HeightAttr {
  Height = 'height',
  MinHeight = 'min-height',
}

export interface VisualProps {
  totalWidth: number; // in rem

  // Width of the magnifying glass icon
  magnifyingGlassWidth: number; // in rem
  // Separation distance between magnifying glass icon and country label
  magnifyingGlassGap: number; // in rem

  magnifyingGlassColor: string;
  placeholderTextColor: string;
  searchBarHeight: number; // value, define unit below
  searchBarHeightUnit: string; // search bar height unit
  placeholderMarginTop: number; // in rem
  placeholderMarginBottom: number; // in rem

  menuMaxHeight: number; // in pixels

  fontSize: string; // include unit
  heightAttr: HeightAttr;
  backgroundColor: string;
}

export interface OptionWithHeight extends Option {
  height: number;
}

type LoadingStatusProps =
  {isLoading: false, options: Option[]} |
  {isLoading: true};

export interface OtherProps {
  selectedCountry: string;
  onCountrySelect: (country: string) => void;
  promptText: string;
  visualProps: VisualProps;

  // For injection in testing:
  getCharacterWidthMap?: typeof defaultGetCharacterWidthMap;
  offscreenRenderingDivID?: string;
  getFluentString: GetString;
  useTriggerButton: boolean;
}

type Props = LoadingStatusProps & OtherProps;

const Dropdown = (props: Props) => {
  const {
    onCountrySelect, selectedCountry, promptText, visualProps,
    getCharacterWidthMap = defaultGetCharacterWidthMap,
    offscreenRenderingDivID = defaultOffscreenRenderingDivID,
    getFluentString, useTriggerButton,
  } = props;

  const onChange = (option: Option) => onCountrySelect(option.value);

  const downshiftInstanceRef = useRef<DownshiftInterfaceWithKeyDownHandlers<Option> | null>(null);
  const inputKeydownHandlerRef = useRef((event: KeyboardEvent) => {
    const {current: downshiftInstance} = downshiftInstanceRef;
    if (downshiftInstance !== null) {
      if (event.keyCode === 38) {
        // handle arrow up. Strictly speaking these handlers take in React
        // synthetic events but because their implementation doesn't use any
        // React-specific property, we can pretend that it's a native DOM event:
        downshiftInstance.keyDownHandlers.ArrowUp.call(downshiftInstance, event as any);
      } else if (event.keyCode === 40) {
        // handle arrow down:
        downshiftInstance.keyDownHandlers.ArrowDown.call(downshiftInstance, event as any);
      } else if(event.keyCode === 32) {

      }
    }
  });

  const [characterWidthMap, setCharacterWidthMap] = useState<Map<string, number> | undefined>(undefined);
  useEffect(() => {
    if (characterWidthMap === undefined) {
      const fetchAndSetCharacterWidthMap = async () => {
        const map = await getCharacterWidthMap();
        setCharacterWidthMap(map);
      };
      try {
        fetchAndSetCharacterWidthMap();
      } catch (e) {
        console.error(e);
      }
    }
  });


  const optionsForHook = (props.isLoading === true) ? undefined : props.options;
  const optionsWithHeight = useMemo(() => {
    if (optionsForHook === undefined || characterWidthMap === undefined) {
      return [];
    } else {
      const maxScrollbarWidth = getMaxScrollbarWidth(offscreenRenderingDivID);
      const textWidth =
        visualProps.totalWidth * rootFontSize -
        getMenuItemLeftPadding(visualProps) * rootFontSize -
        flagWidth - flagToTextSeparation - maxScrollbarWidth;

      const result: OptionWithHeight[] = [];
      for (const option of optionsForHook) {
        // To avoid underestimating the height, we'll reduce the width of the
        // container available to us for text layout:
        const safetyMargin = 0.9;
        const measurementResult = getTotalTextHeight({
          text: option.label,
          containerWidthInPixels: textWidth * safetyMargin,
          lineHeightFactor: menuItemLineHeight,
          fontSize: countryNameFontSize,
          characterWidthMap,
        });
        if (measurementResult.success === true) {
          const verticalPadding = 4; // in pixels
          const optionWithHeight: OptionWithHeight = {
            ...option,
            height: measurementResult.height + verticalPadding * 2,
          };
          result.push(optionWithHeight);
        }
      }
      return result;
    }
  }, [optionsForHook, characterWidthMap]);

  // Downshift's controlled prop:
  const [inputValue, actualSetInputValue] = useState<string>('');

  // Need this indirection because `downshift` calls the `onInputValueChange`
  // callback with 2 arguments, which will trigger a React warning if passed
  // directly to the setState callback.
  const setInputValue =
    (value: string, _unused: ControllerStateAndHelpers<Option>) => actualSetInputValue(value);

  if (props.isLoading === true || characterWidthMap === undefined) {
    return (
      <Prompt
        isLoading={true}
        visualProps={visualProps}
      />
    );
  } else {
    const {options} = props;
    const foundIndex = options.findIndex(option => option.value === selectedCountry);
    if (foundIndex === undefined) {
      throw new Error('Cannot find option corresponding to country ID ' + selectedCountry);
    }
    const defaultHighlightedIndex = foundIndex;

    let filteredOptions: OptionWithHeight[];
    if (inputValue === '') {
      filteredOptions = optionsWithHeight;
    } else {
      filteredOptions = optionsWithHeight.filter(
        option => option.searchString.includes(inputValue.toLowerCase()),
      );
    }
    const itemCount = filteredOptions.length;
    const selectedItem = optionsWithHeight[foundIndex];

    const renderProp = getRenderProp({
      promptText, selectedItem,
      options: filteredOptions,
      inputKeydownHandler: inputKeydownHandlerRef.current,
      visualProps, getFluentString, useTriggerButton,
    });

    // This is a workdaround that always return an empty string so that when the
    // user clicks into the placeholder (with an option already selected), the
    // user sees the "prompt text" (e.g. "select a location") instead of the
    // string value of the selected country:
    const itemToString = () => '';

    return (
      <Downshift
        defaultHighlightedIndex={defaultHighlightedIndex}
        children={renderProp}
        itemToString={itemToString}
        onChange={onChange}
        onInputValueChange={setInputValue}
        selectedItem={selectedItem}
        itemCount={itemCount}
        inputValue={inputValue}
        ref={downshiftInstanceRef as any}
      />
    );
  }
};

export default Dropdown;
